import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import HeroImage from '../assets/samantha-photo.jpg';

const Hero = () => {
  return (
    <HeroContainer>
      <HeroBackground>
        <HeroBackgroundShapes>
          <motion.div 
            className="shape shape-1"
            animate={{ 
              y: [0, -20, 0], 
              opacity: [0.5, 0.8, 0.5] 
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 15, 
              ease: "easeInOut" 
            }}
          />
          <motion.div 
            className="shape shape-2"
            animate={{ 
              y: [0, 20, 0], 
              opacity: [0.3, 0.6, 0.3] 
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 20, 
              ease: "easeInOut" 
            }}
          />
        </HeroBackgroundShapes>
      </HeroBackground>
      
      <div className="container">
        <HeroContent>
          <motion.div 
            className="content-wrapper"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTagline>Professional Development Coach</HeroTagline>
            {/* <HeroHeadline>
              Transform Your <span>Potential</span> Into <span>Performance</span>
            </HeroHeadline> */}
            <HeroHeadline>
              Transform Your Potential Into Performance
            </HeroHeadline>
            <HeroSubheading>
              Coaching that delivers clarity, confidence and measurable results
            </HeroSubheading>
            <HeroButtons>
              <PrimaryButton>
                <Link to="booking" smooth={true} duration={800}>
                  Book Your Coaching Session
                </Link>
              </PrimaryButton>
              <SecondaryButton>
                <Link to="services" smooth={true} duration={800}>
                  Explore Services
                </Link>
              </SecondaryButton>
            </HeroButtons>
            {/* <HeroTrustBadge>
              Trusted by leaders across finance, technology, and professional services
            </HeroTrustBadge> */}
          </motion.div>
        </HeroContent>
        
        <HeroImageWrapper>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <HeroImageFrame>
              <img src={HeroImage} alt="Samantha Knowles" />
            </HeroImageFrame>
          </motion.div>
        </HeroImageWrapper>
      </div>
      
      <DiagonalDivider />
    </HeroContainer>
  );
};

const HeroContainer = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 120px 0 100px;
  overflow: hidden;
  
  .container {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    padding: 150px 0 80px;
    
    .container {
      flex-direction: column;
    }
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #f9f9f9 0%, #f3f3f3 100%);
  z-index: 1;
`;

const HeroBackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  
  .shape {
    position: absolute;
    border-radius: 50%;
  }
  
  .shape-1 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(168, 196, 160, 0.1) 0%, rgba(168, 196, 160, 0) 70%);
    top: -100px;
    right: -100px;
  }
  
  .shape-2 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(90, 127, 140, 0.08) 0%, rgba(90, 127, 140, 0) 70%);
    bottom: -200px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  flex: 0.55; /* Changed from 0.8 to 0.55 to give more space to the image */
  padding-right: 60px; /* Slightly reduced from 80px to 60px */
  
  .content-wrapper {
    max-width: 650px;
  }
  
  @media (max-width: 1024px) {
    padding-right: 40px;
  }
  
  @media (max-width: 768px) {
    padding-right: 0;
    text-align: center;
    
    .content-wrapper {
      margin: 0 auto;
    }
  }
`;

const HeroTagline = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #5A7F8C;
  margin-bottom: 20px;
`;

const HeroHeadline = styled.h1`
  font-size: 4.5rem;
  line-height: 1.1;
  margin-bottom: 25px;
  color: #1A2A3A;
  font-weight: 700;
  
  span {
    position: relative;
    color: #5A7F8C;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 5px;
      left: 0;
      width: 100%;
      height: 6px;
      background-color: rgba(168, 196, 160, 0.3);
      z-index: -1;
    }
  }
  
  @media (max-width: 1024px) {
    font-size: 3.8rem;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const HeroSubheading = styled.p`
  font-size: 1.4rem;
  color: #555;
  margin-bottom: 35px;
  font-weight: 300;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 35px;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.div`
  a {
    display: inline-block;
    padding: 16px 32px;
    background-color: #A8C4A0;
    color: white;
    text-decoration: none;
    border-radius: 5px;
    font-weight: 600;
    font-size: 1.1rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(168, 196, 160, 0.4);
    
    &:hover {
      background-color: #97b38f;
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(168, 196, 160, 0.5);
    }
  }
`;

const SecondaryButton = styled.div`
  a {
    display: inline-block;
    padding: 16px 32px;
    background-color: transparent;
    color: #5A7F8C;
    text-decoration: none;
    border-radius: 5px;
    font-weight: 600;
    font-size: 1.1rem;
    transition: all 0.3s ease;
    border: 2px solid #5A7F8C;
    
    &:hover {
      background-color: rgba(90, 127, 140, 0.1);
      transform: translateY(-3px);
    }
  }
`;

const HeroTrustBadge = styled.div`
  font-size: 0.9rem;
  color: #777;
  position: relative;
  padding-top: 25px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: #ddd;
  }
  
  @media (max-width: 768px) {
    &:before {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const HeroImageWrapper = styled.div`
  flex: 0.45; /* Changed from 0.2 to 0.45 - making the image much more prominent */
  position: relative;
  z-index: 3;
  
  @media (max-width: 768px) {
    margin-top: 60px;
    width: 80%; /* Changed from 300px to 80% for a larger, more responsive image */
    max-width: 450px; /* Added max-width for very large screens */
  }
`;

const HeroImageFrame = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px; /* Increased from -15px to -20px */
    right: -20px; /* Increased from -15px to -20px */
    width: 100%;
    height: 100%;
    border: 2px solid #A8C4A0;
    border-radius: 10px;
    z-index: -1;
  }
  
  img {
    width: 100%;
    height: auto;
    border-radius: 10px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.18); /* Increased shadow for more depth */
    filter: saturate(0.9) brightness(1.05);
  }
`;

const DiagonalDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-color: #f9f9f9;
  clip-path: polygon(0 100%, 100% 60%, 100% 100%, 0 100%);
  z-index: 3;
`;

export default Hero;